import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { AppShell, useShell } from '../components/AppShell';
import { useEntitlements } from '../lib/entitlements';
import DataExport from '../components/DataExport';
import { EarningsCard } from '../components/EarningsCard';
import { rangeForPeriod, EARNINGS_PERIODS } from '../lib/earnings';

// Heatmap palette (light → dark) — values pulled from mockup
const HEAT_PALETTE = ['#f5f4ff', '#eceaff', '#c9c4ff', '#b3abff', '#7d77dd', '#5b52d6', '#3a32d6'];

// Service progress-bar palette in the mockup: indigo, indigo, green, amber, then repeats
const SERVICE_COLOURS = ['var(--acc)', 'var(--acc)', 'var(--green)', 'var(--amber)'];

const TIME_BANDS = [
  { label: '9–11',  startHr: 9,  endHr: 11 },
  { label: '11–14', startHr: 11, endHr: 14 },
  { label: '14–17', startHr: 14, endHr: 17 },
  { label: '17–20', startHr: 17, endHr: 20 },
];
// Mon..Sat — Sunday hidden per mockup
const DAYS = [
  { label: 'Mon', dow: 1 },
  { label: 'Tue', dow: 2 },
  { label: 'Wed', dow: 3 },
  { label: 'Thu', dow: 4 },
  { label: 'Fri', dow: 5 },
  { label: 'Sat', dow: 6 },
];

const PERIOD_OPTIONS = [
  { key: 'week',  minPlan: 'standard' },
  { key: 'month', minPlan: 'standard' },
  { key: '3m',    minPlan: 'premium'  },
  { key: '6m',    minPlan: 'premium'  },
  { key: '9m',    minPlan: 'premium'  },
  { key: 'year',  minPlan: 'premium'  },
];
// Labels are sourced from EARNINGS_PERIODS so the outer page dropdown
// and the Earnings card dropdown always read the same. (Fixes M1 —
// "This week" now means the same window across the whole page.)
const periodLabel = (key) =>
  EARNINGS_PERIODS.find(p => p.key === key)?.label || key;

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const addDays    = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `£${(v / 1000).toFixed(1)}k`;
  return `£${v.toFixed(0)}`;
};
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;
const pctDelta = (cur, prev) => (prev ? ((cur - prev) / prev) * 100 : 0);
const ptsDelta = (cur, prev) => (cur - prev);

const heatColour = (v, max) => {
  if (!max || v <= 0) return HEAT_PALETTE[0];
  const ratio = v / max;
  const idx = Math.min(HEAT_PALETTE.length - 1, Math.floor(ratio * HEAT_PALETTE.length));
  return HEAT_PALETTE[idx];
};

const AnalyticsInner = () => {
  const { profile } = useShell();
  const { plan } = useEntitlements();
  const shopId = profile?.shop_id;
  const periods = PERIOD_OPTIONS.filter(p => (
    plan === 'premium' ? true : p.minPlan === 'standard'
  ));
  const [periodKey, setPeriodKey] = useState(periods[0].key);

  const [barbers, setBarbers] = useState([]);
  const [barberId, setBarberId] = useState('');   // '' = all barbers
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    bookings:        { v: 0, deltaPct: 0 },
    depositRevenue:  { v: 0, deltaPct: 0 },
    noShowRate:      { v: 0, deltaPts: 0 },
    repeatRate:      { v: 0, deltaPts: 0 },
  });
  const [weekBars, setWeekBars]     = useState([]);     // [{label, count}]
  const [topServices, setTopServices] = useState([]);   // [{name, count, pct}]
  const [heat, setHeat]             = useState([]);     // 2D: [band][day] = count

  // If user is on Standard but plan list grew, keep within cap
  useEffect(() => {
    if (!periods.find(p => p.key === periodKey)) {
      setPeriodKey(periods[periods.length - 1].key);
    }
  }, [plan, periods, periodKey]);

  useEffect(() => {
    if (!shopId) return;
    load(shopId, periodKey, barberId);
  }, [shopId, periodKey, barberId]);

  // Barber list for the filter dropdown — one round-trip per shop.
  useEffect(() => {
    if (!shopId) return;
    (async () => {
      const { data } = await supabase
        .from('barbers')
        .select('id,name')
        .eq('shop_id', shopId)
        .eq('active', true)
        .order('created_at');
      setBarbers(data || []);
    })();
  }, [shopId]);

  const load = async (sid, pKey, bid) => {
    setLoading(true);

    // ----- Window (M1): use the SAME resolver the Earnings card uses,
    // so "This week"/"This month" mean the same thing across every card
    // on the page (calendar-anchored for week/month, rolling-months for
    // 3m/6m/9m/year).
    const { from: winStart, to: winEnd } = rangeForPeriod(pKey);
    // Previous window = same length ending just before the current one.
    const winLenMs = winEnd.getTime() - winStart.getTime();
    const prevEnd  = new Date(winStart.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - winLenMs);

    // Pull all bookings for current + previous window in one go
    let q = supabase
      .from('bookings')
      .select('id,customer_id,starts_at,status,deposit_paid,deposit_amount,service:services(name)')
      .eq('shop_id', sid)
      .gte('starts_at', prevStart.toISOString())
      .lte('starts_at', winEnd.toISOString());
    if (bid) q = q.eq('barber_id', bid);
    const { data: rows } = await q;

    const all = rows || [];
    const inWindow = (b, s, e) => {
      const t = new Date(b.starts_at).getTime();
      return t >= s.getTime() && t <= e.getTime();
    };
    const cur  = all.filter(b => inWindow(b, winStart, winEnd));
    const prev = all.filter(b => inWindow(b, prevStart, prevEnd));

    // ===== KPIs =====
    const totalCur = cur.filter(b => b.status !== 'cancelled').length;
    const totalPrev = prev.filter(b => b.status !== 'cancelled').length;

    // ----- Deposit revenue (M2): key on WHEN the deposit was captured,
    // not when the appointment is scheduled. There's no dedicated
    // `deposit_paid_at` column, so we use `created_at` as the proxy —
    // deposits are captured within seconds of booking creation in the
    // anon flow (Stripe webhook fires immediately), and admin-created
    // bookings mark deposit_paid at insert time. If/when a
    // deposit_paid_at column ships, swap the timestamp field here.
    let depQ = supabase
      .from('bookings')
      .select('deposit_amount,created_at')
      .eq('shop_id', sid)
      .eq('deposit_paid', true)
      .gte('created_at', prevStart.toISOString())
      .lte('created_at', winEnd.toISOString());
    if (bid) depQ = depQ.eq('barber_id', bid);
    const { data: depRows } = await depQ;
    const depInWindow = (r, s, e) => {
      const t = new Date(r.created_at).getTime();
      return t >= s.getTime() && t <= e.getTime();
    };
    const depCur = (depRows || [])
      .filter(r => depInWindow(r, winStart, winEnd))
      .reduce((s, r) => s + Number(r.deposit_amount || 0), 0);
    const depPrev = (depRows || [])
      .filter(r => depInWindow(r, prevStart, prevEnd))
      .reduce((s, r) => s + Number(r.deposit_amount || 0), 0);

    const nsCurDone  = cur.filter(b => b.status === 'completed' || b.status === 'no_show').length;
    const nsCurNo    = cur.filter(b => b.status === 'no_show').length;
    const nsPrevDone = prev.filter(b => b.status === 'completed' || b.status === 'no_show').length;
    const nsPrevNo   = prev.filter(b => b.status === 'no_show').length;
    const nsRateCur  = nsCurDone ? (nsCurNo / nsCurDone) * 100 : 0;
    const nsRatePrev = nsPrevDone ? (nsPrevNo / nsPrevDone) * 100 : 0;

    // Repeat rate: % of customers whose lifetime booking count is >1.
    // Pull lifetime counts ONLY for customers seen in the current window.
    const custIds = Array.from(new Set(cur.map(b => b.customer_id)));
    let repeatRateCur = 0;
    if (custIds.length) {
      const { data: lifetime } = await supabase
        .from('bookings').select('customer_id')
        .eq('shop_id', sid).in('customer_id', custIds);
      const counts = {};
      (lifetime || []).forEach(r => { counts[r.customer_id] = (counts[r.customer_id] || 0) + 1; });
      const repeats = custIds.filter(id => (counts[id] || 0) > 1).length;
      repeatRateCur = (repeats / custIds.length) * 100;
    }
    // Previous-window repeat rate
    const custIdsPrev = Array.from(new Set(prev.map(b => b.customer_id)));
    let repeatRatePrev = 0;
    if (custIdsPrev.length) {
      const { data: lifetime } = await supabase
        .from('bookings').select('customer_id,starts_at')
        .eq('shop_id', sid).in('customer_id', custIdsPrev)
        .lte('starts_at', prevEnd.toISOString());
      const counts = {};
      (lifetime || []).forEach(r => { counts[r.customer_id] = (counts[r.customer_id] || 0) + 1; });
      const repeats = custIdsPrev.filter(id => (counts[id] || 0) > 1).length;
      repeatRatePrev = (repeats / custIdsPrev.length) * 100;
    }

    setKpis({
      bookings:       { v: totalCur,    deltaPct: pctDelta(totalCur, totalPrev) },
      depositRevenue: { v: depCur,      deltaPct: pctDelta(depCur, depPrev) },
      noShowRate:     { v: nsRateCur,   deltaPts: ptsDelta(nsRateCur, nsRatePrev) },
      repeatRate:     { v: repeatRateCur, deltaPts: ptsDelta(repeatRateCur, repeatRatePrev) },
    });

    // ===== Bookings by week =====
    // Bucket into weeks of 7 days, oldest → newest, label W1..Wn
    const nDays = Math.max(1, Math.round((winEnd.getTime() - winStart.getTime()) / 86400000));
    const weekCount = Math.max(1, Math.ceil(nDays / 7));
    const bars = [];
    for (let i = 0; i < weekCount; i++) {
      const bs = startOfDay(addDays(winStart, i * 7));
      const be = endOfDay(addDays(bs, 6));
      const c  = cur.filter(b => inWindow(b, bs, be) && b.status !== 'cancelled').length;
      bars.push({ label: `W${i + 1}`, count: c });
    }
    setWeekBars(bars);

    // ===== Top services =====
    const svcCounts = {};
    cur.forEach(b => {
      if (b.status === 'cancelled') return;
      const name = b.service?.name || '—';
      svcCounts[name] = (svcCounts[name] || 0) + 1;
    });
    const total = Object.values(svcCounts).reduce((s, n) => s + n, 0) || 1;
    const services = Object.entries(svcCounts)
      .map(([name, count]) => ({ name, count, pct: Math.round((count / total) * 100) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
    setTopServices(services);

    // ===== Busiest times heatmap =====
    const grid = TIME_BANDS.map(() => DAYS.map(() => 0));
    cur.forEach(b => {
      if (b.status === 'cancelled') return;
      const d = new Date(b.starts_at);
      const dow = d.getDay();   // 0=Sun..6=Sat
      const hr  = d.getHours();
      const dayIdx = DAYS.findIndex(x => x.dow === dow);
      if (dayIdx === -1) return;
      const bandIdx = TIME_BANDS.findIndex(b2 => hr >= b2.startHr && hr < b2.endHr);
      if (bandIdx === -1) return;
      grid[bandIdx][dayIdx] += 1;
    });
    setHeat(grid);

    setLoading(false);
  };

  const heatMax = useMemo(() => {
    let m = 0;
    heat.forEach(row => row.forEach(v => { if (v > m) m = v; }));
    return m;
  }, [heat]);

  const maxBar = Math.max(1, ...weekBars.map(w => w.count));

  const periodTitle = periodLabel(periodKey);

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2>Analytics</h2>
          <div className="sub">
            {periodTitle} · <span style={{ textTransform: 'capitalize' }}>{plan}</span>
          </div>
        </div>
        <div className="period-pick" data-testid="period-pick" style={{ display: 'flex', gap: 8 }}>
          {barbers.length > 1 && (
            <select
              value={barberId}
              onChange={(e) => setBarberId(e.target.value)}
              data-testid="analytics-barber-select"
              aria-label="Filter by barber"
            >
              <option value="">All barbers</option>
              {barbers.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
          <select
            value={periodKey}
            onChange={(e) => setPeriodKey(e.target.value)}
            data-testid="period-select"
          >
            {periods.map(p => (
              <option key={p.key} value={p.key}>{periodLabel(p.key)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="body">
        {/* Earnings card lives OUTSIDE the loading gate so the page-level
            filter never unmounts it — the chart's own period selector
            stays intact when the shop admin changes the outer filter. */}
        <EarningsCard
          shopId={shopId}
          plan={plan}
          title="Earnings"
          subtitle="Full payments only — deposits excluded"
          defaultPeriod="week"
          dataTestId="analytics-earnings"
        />

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
        ) : (
          <>
            {/* KPI ROW */}
            <div className="a-kpis" style={{ marginTop: 14 }}>
              <div className="kpi" data-testid="kpi-total">
                <div className="t">Total bookings</div>
                <div className="v">{kpis.bookings.v}</div>
                <DeltaPct value={kpis.bookings.deltaPct} better="up" />
              </div>
              <div className="kpi" data-testid="kpi-deposit">
                <div className="t">Deposit revenue</div>
                <div className="v">{fmtMoney(kpis.depositRevenue.v)}</div>
                <DeltaPct value={kpis.depositRevenue.deltaPct} better="up" />
              </div>
              <div className="kpi" data-testid="kpi-noshow">
                <div className="t">No-show rate</div>
                <div className="v">{fmtPct(kpis.noShowRate.v)}</div>
                <DeltaPts value={kpis.noShowRate.deltaPts} better="down" />
              </div>
              <div className="kpi" data-testid="kpi-repeat">
                <div className="t">Repeat rate</div>
                <div className="v">{fmtPct(kpis.repeatRate.v)}</div>
                <DeltaPts value={kpis.repeatRate.deltaPts} better="up" />
              </div>
            </div>

            {/* CHARTS ROW */}
            <div className="a-cols">
              <div className="panel" data-testid="top-services">
                <div className="ph"><h3>Top services</h3></div>
                <div className="prog">
                  {topServices.length === 0 ? (
                    <div style={{ padding: '12px 0', color: 'var(--mute)', fontSize: 12.5 }}>
                      No bookings in this window.
                    </div>
                  ) : topServices.map((s, i) => (
                    <div key={s.name} className="progrow">
                      <div className="l"><b>{s.name}</b><span>{s.pct}%</span></div>
                      <div className="track">
                        <div className="fl" style={{ width: `${s.pct}%`, background: SERVICE_COLOURS[i] }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* HEATMAP */}
            <div className="panel" style={{ marginTop: 14 }} data-testid="busiest-times">
              <div className="ph">
                <h3>Busiest times</h3>
                <span style={{ fontSize: 12, color: 'var(--mute)' }}>Darker = busier</span>
              </div>
              <div className="heat">
                <span className="lab" />
                {DAYS.map(d => (
                  <span key={d.dow} className="lab" style={{ justifyContent: 'center' }}>{d.label}</span>
                ))}
                {TIME_BANDS.map((band, bi) => (
                  <React.Fragment key={band.label}>
                    <span className="lab">{band.label}</span>
                    {DAYS.map((d, di) => (
                      <span
                        key={d.dow}
                        className="cell"
                        style={{ background: heatColour(heat[bi]?.[di] || 0, heatMax) }}
                        title={`${d.label} ${band.label} · ${heat[bi]?.[di] || 0} booking${(heat[bi]?.[di] || 0) === 1 ? '' : 's'}`}
                        data-testid={`heat-${bi}-${di}`}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* DATA EXPORT — shop_admin only */}
            {profile?.role === 'shop_admin' && shopId && (
              <DataExport shopId={shopId} />
            )}
          </>
        )}
      </div>
    </main>
  );
};

const DeltaPct = ({ value, better }) => {
  const v = Number(value || 0);
  if (!isFinite(v) || Math.abs(v) < 0.05) return <div className="d flat">—</div>;
  const cls = better === 'down' ? (v <= 0 ? 'up' : 'dn') : (v >= 0 ? 'up' : 'dn');
  return (
    <div className={`d ${cls}`}>
      {v >= 0 ? `▲ ${v.toFixed(0)}%` : `▼ ${Math.abs(v).toFixed(0)}%`}
    </div>
  );
};
const DeltaPts = ({ value, better }) => {
  const v = Number(value || 0);
  if (!isFinite(v) || Math.abs(v) < 0.05) return <div className="d flat">—</div>;
  const cls = better === 'down' ? (v <= 0 ? 'up' : 'dn') : (v >= 0 ? 'up' : 'dn');
  return (
    <div className={`d ${cls}`}>
      {v >= 0 ? `▲ ${v.toFixed(1)}pts` : `▼ ${Math.abs(v).toFixed(1)}pts`}
    </div>
  );
};

const Analytics = () => (
  <AppShell>
    <AnalyticsInner />
  </AppShell>
);

export default Analytics;
